import { TAU } from '../core/math';
import { RNG } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { disposeGadget, makeDrone, makeFieldRing, makeOrbitBlades, setBladeRadius, updateFieldRing } from '../render/gadgets';
import { areaDamage, damageEnemy, spawnRing } from '../sim/fx';
import { spawnBullet } from '../sim/weapons';
import { P } from '../world/player';
import type { Union } from './unions';
/* ============================================================
   融合 · 第二批（2026-08-05）· 持續場域
   ============================================================

   機制與整批的動機寫在 `data/unions.ts`，這裡只有定義。
   這三個都有 `update`，所以三件事一定要做對，否則會踩上一輪的坑：

     1. `onRemove` 要 `disposeGadget` 每一個造型 —— 融合被別的融合取代時
        （一把武裝可以參與多個融合）殘骸會留在場上。
     2. 相位累積用的角度**不要自己寫取模**（`sim/fx.ts` 的 `angDiff`），
        負數取模在 JS 會回負值，扇形判定會在幾十圈之後永久失效。
     3. 再命中冷卻要有 —— 每幀都結算的接觸傷害等於無限 DPS。

   2026-08-05 加等級（`CRAFT_MAX`，契約見 `.claude/notes/craft-levels.md`）：
   Lv1 逐位元不變，Lv2 吞吐 +25～35%，Lv3 再解一條限制 ＋ 可再 +10～15%。 */

export var UNIONS_C: Union[] = [
  {
    /* 斥力力場 + 環繞旋刃 → 絞盤領域。
       **兩把單獨做不到的**：力場的傷害只在腳邊、旋刃的傷害只在那一圈上，
       兩把都有「一個半徑」的天花板。合起來變成**兩層**：
       外圈的刃把人擋下來、內圈的場把人磨掉，中間那段是死區。
       它是這個池子裡唯一「站著不動就會贏」的東西，代價是你哪裡都去不了。 */
    id: 'u-grinder', n: '絞盤領域', kind: '融合', axes: ['範圍', '持續'],
    union: { a: 'w-aura', b: 'w-orbit' },
    turn: 3, turnD: '兩圈之間是死區，站著不痛 → 外圈加寬封死、貼到內圈，死區消失',
    d: function (lv) {
      return '雙層絞盤：外圈六片旋刃切割 ' + (lv < 2 ? 88 : lv < 3 ? 114 : 154)
        + '，內圈力場持續研磨 ' + (lv < 2 ? 77 : lv < 3 ? 100 : 135)
        + (lv < 3 ? '，敵人卡在中間' : '；**外圈加寬貼住內圈，死區封死，站中間也在磨**');
    },
    onEquip: function (o) {
      /* 外圈**整圈判定**、半寬 2.2（見下面的 `Math.abs(d - Rout) > outerHalf + e.r`）。 */
      o.st = { vis: makeFieldRing(0xff8a3d), blades: makeOrbitBlades(6, 0xd8dee6, { width: 2.2 }), a: 0, t: 0, hits: [] };
    },
    onRemove: function (o) {
      if (o.st) { disposeGadget(o.st.vis); disposeGadget(o.st.blades); }
      o.st = null;
    },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var st = o.st, ar = P.s.area || 1;
      var Rin = 6.5 * ar, Rout = 11.5 * ar;
      /* 等級（2026-08-05）：
           Lv2  外圈 88→114、內圈 77→100（吞吐 +30%），半徑與轉速不動
           Lv3  再加碼（外→154、內→135，量到 Lv3/Lv1 才夠 1.6× 帶寬）；
                外圈半寬 2.2→5.0，正好貼到 Rin —— 中間那段「死區」封起來，
                兩層連成一片全滿判定 */
      var outerDmg = lv < 2 ? 88 : (lv < 3 ? 114 : 154);
      var innerDmg = lv < 2 ? 77 : (lv < 3 ? 100 : 135);
      var outerHalf = lv < 3 ? 2.2 : 5.0;
      st.a += dt * 3.4;
      updateFieldRing(st.vis, P.pos.x, P.pos.z, Rin, dt);
      st.blades.g.position.set(P.pos.x, 0, P.pos.z);
      setBladeRadius(st.blades, Rout);
      for (var b = 0; b < 6; b++) {
        st.blades.blades[b].visible = true;
        st.blades.blades[b].rotation.y = st.a + (b / 6) * TAU;
      }
      for (var h = st.hits.length - 1; h >= 0; h--) {
        st.hits[h].t -= dt;
        if (st.hits[h].t <= 0) st.hits.splice(h, 1);
      }
      /* 外圈：**整個環帶**都判定，不逐片刀刃比角度。
         六片刀刃在 11.5 半徑上的間隔已經比敵人的體積小，
         逐片判定只會多一個「角度累積」的坑而讀起來完全一樣。 */
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
        if (Math.abs(d - Rout) > outerHalf + e.r) continue;
        var busy = false;
        for (var q = 0; q < st.hits.length; q++) if (st.hits[q].e === e) { busy = true; break; }
        if (busy) continue;
        st.hits.push({ e: e, t: 0.4 });
        var kk = 1 / Math.max(0.6, d);
        /* 外圈**往內打**：把人推進內圈的磨盤裡，而不是踢出去。
           這是「絞盤」這個名字的實作 —— 兩層要互相餵，不然只是兩個獨立的環。 */
        damageEnemy(e, outerDmg, false, -dx * kk * 9, -dz * kk * 9, 'vortex', undefined, undefined, 'u-grinder');
      }
      st.t -= dt;
      if (st.t > 0) return;
      st.t = 0.3;
      spawnRing(P.pos.x, P.pos.z, 0xff8a3d, Rin * 1.05, Rin * 0.35, 0.26);
      for (var j = enemies.length - 1; j >= 0; j--) {
        if (j >= enemies.length) j = enemies.length - 1;
        if (j < 0) break;
        var e2 = enemies[j]; if (!e2) continue;
        var dx2 = e2.pos.x - P.pos.x, dz2 = e2.pos.z - P.pos.z;
        if (Math.hypot(dx2, dz2) > Rin + e2.r) continue;
        // ⚠️ 配平：外圈 66 → 88、內圈 58 → 77（第一版 ×2.41）。雙層看起來很兇，
        // 但兩層的再命中冷卻各自獨立，實際結算次數比單層的來源多不了多少。
        // 等級成長寫在上面 `innerDmg`。
        damageEnemy(e2, innerDmg, false, 0, 0, 'vortex', undefined, undefined, 'u-grinder');
      }
    }
  },
  {
    /* 衝擊斬波 + 環繞旋刃 → 斬輪。
       **兩把單獨做不到的**：斬波朝著你的面向射（自動瞄準決定），
       旋刃只在固定半徑上轉。合起來之後**斬波從旋刃的位置沿切線甩出去**——
       彈道是螺旋的，覆蓋在你身邊展開而不是往某個方向延伸。
       跟 `u-grinder` 用同一把來源（`w-orbit`）但方向完全相反：
       絞盤是把人留在身邊，斬輪是把輸出甩到遠處。 */
    id: 'u-bladewheel', n: '斬輪', kind: '融合', axes: ['範圍', '冷卻'],
    union: { a: 'w-pulse', b: 'w-orbit' },
    turn: 3, turnD: '貫穿只在敵人排成一直線時有用（空曠地圖幾乎不會排線） → 換成跳彈，不用排線也吃得到',
    d: function (lv) {
      return '三片旋刃高速環繞（接觸 ' + (lv < 2 ? 40 : lv < 3 ? 52 : 72) + '），並不斷沿切線甩出 '
        + (lv < 3 ? 96 : 130) + ' 傷害的穿透斬波，彈道呈螺旋展開'
        + (lv < 3 ? '' : '；**斬波換成跳彈，敵人不用排成一直線也打得到**');
    },
    /* 同樣沒有角度判定，半寬 2.0。 */
    onEquip: function (o) { o.st = { vis: makeOrbitBlades(3, 0x9fffe8, { width: 2.0 }), a: 0, cd: 0, hits: [] }; },
    onRemove: function (o) { if (o.st) disposeGadget(o.st.vis); o.st = null; },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var st = o.st, R = 7.5 * (P.s.area || 1);
      /* 等級（2026-08-05）：
           Lv2  斬波冷卻 0.42→0.32（吞吐 +31%），接觸 40→52
           Lv3  冷卻再 →0.28、斬波 96→130、接觸 →72（量到 Lv3/Lv1 才夠帶寬）；
                斬波從**無限貫穿**換成**跳彈 5 下**（見下面 `pierce`/`chain`） */
      var fireCd = lv < 2 ? 0.42 : (lv < 3 ? 0.32 : 0.28);
      var boltDmg = lv < 3 ? 96 : 130;
      var contactDmg = lv < 2 ? 40 : (lv < 3 ? 52 : 72);
      st.a += dt * 5.0;
      st.vis.g.position.set(P.pos.x, 0, P.pos.z);
      setBladeRadius(st.vis, R);
      for (var b = 0; b < 3; b++) {
        st.vis.blades[b].visible = true;
        st.vis.blades[b].rotation.y = st.a + (b / 3) * TAU;
      }
      for (var h = st.hits.length - 1; h >= 0; h--) {
        st.hits[h].t -= dt;
        if (st.hits[h].t <= 0) st.hits.splice(h, 1);
      }
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
        if (Math.abs(d - R) > 2.0 + e.r) continue;
        var busy = false;
        for (var q = 0; q < st.hits.length; q++) if (st.hits[q].e === e) { busy = true; break; }
        if (busy) continue;
        st.hits.push({ e: e, t: 0.4 });
        var kk = 1 / Math.max(0.6, d);
        damageEnemy(e, contactDmg, false, dx * kk * 6, dz * kk * 6, 'vortex', undefined, undefined, 'u-bladewheel');
      }
      st.cd -= dt;
      if (st.cd > 0) return;
      st.cd = fireCd;
      /* 從**刀刃當下的位置**沿切線射出。從玩家身上射的話彈道是放射狀，
         看起來跟 `e-omni` 一樣；從刀尖甩出去才會讀成「轉輪在甩東西」。 */
      var crit = RNG.next() < P.s.crit;
      /* Lv3 拿掉的第二條限制：**貫穿只在敵人排成一直線時有用**。這張地圖
         刻意保持空曠（敵人幾乎不會自己排成一線），無限貫穿因此形同虛設。
         Lv3 換成 `chain = 5`：不用排線，敵人只要在附近就跳得到。
         ⚠️ `chain > 0` 時 `pierce` 永遠不生效，兩者互斥，只能二選一。 */
      var pierce = lv < 3 ? 99 : 0, chain = lv < 3 ? 0 : 5;
      for (var k = 0; k < 3; k++) {
        var ba = st.a + (k / 3) * TAU;
        var ox = P.pos.x + Math.sin(ba) * R, oz = P.pos.z + Math.cos(ba) * R;
        spawnBullet(ox, oz, ba + Math.PI / 2, boltDmg * (crit ? P.s.critMul : 1), crit, 40, pierce, chain, 1.8, 0.55, 'u-bladewheel');
      }
      SFX.slash();
    }
  },
  {
    /* 感應無人機 + 跳彈迴路 → 僚機電網。
       **兩把單獨做不到的**：無人機是三個各自為政的點，跳彈需要有「下一個目標」。
       合起來之後**三台之間拉起電弧**——輸出不再發生在無人機身上，
       而是發生在它們**圍出來的三角形**上，那是一張會跟著你移動的網。 */
    id: 'u-swarmnet', n: '僚機電網', kind: '融合', axes: ['範圍', '傷害'],
    union: { a: 'w-drone', b: 'w-chain' },
    turn: 3, turnD: '電弧只在三角形邊線上，內部（你和僚機之間）完全安全 → 加三條輻射線接回你身上',
    d: function (lv) {
      return '三台僚機之間拉起高壓電弧（每點 ' + (lv < 2 ? 10 : lv < 3 ? 12 : 13) + '），圍成一張跟著你移動的網'
        + (lv < 3 ? '' : '；**再從你身上多拉三條輻射電弧到每台僚機，網子中心也通電**');
    },
    onEquip: function (o) {
      o.st = { d: [makeDrone(0x5fe3ff), makeDrone(0xffd24a), makeDrone(0x9fffe8)], a: 0, t: 0 };
    },
    onRemove: function (o) {
      if (o.st) for (var i = 0; i < o.st.d.length; i++) disposeGadget(o.st.d[i]);
      o.st = null;
    },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var st = o.st, R = 9.5 * (P.s.area || 1);
      /* 等級（2026-08-05）：
           Lv2  每點傷害 10→13（吞吐 +30%）
           Lv3  →15；**再加三條「你 → 僚機」的輻射電弧**（見下），
                原本網子中心（你跟僚機之間）完全安全，這裡補上 */
      /* ⚠️ 2026-08-05 收斂：10/13/17 量到成長 ×2.09（帶寬上緣 1.9×）——
         電弧是逐點結算的，每點加傷會被「點數 × 條數」放大兩次。 */
      var dmg = lv < 2 ? 10 : (lv < 3 ? 12 : 13);
      st.a += dt * 1.3;
      var pts: any[] = [];
      for (var i = 0; i < 3; i++) {
        var ang = st.a + (i / 3) * TAU;
        var x = P.pos.x + Math.cos(ang) * R, z = P.pos.z + Math.sin(ang) * R;
        st.d[i].g.position.set(x, 2.8 + Math.sin(st.a * 2 + i) * 0.3, z);
        st.d[i].g.rotation.y = -ang;
        st.d[i].muzzle.visible = false;
        pts.push({ x: x, z: z });
      }
      st.t -= dt;
      if (st.t > 0) return;
      st.t = 0.28;
      /* 電弧用「沿線灑一串小範圍傷害」實作，不做線段碰撞
         （同 `u-lattice` 的作法）：讀起來是一條線，算起來還是點，
         而且 `areaDamage` 已經處理過池化與特效配額。 */
      for (var a = 0; a < 3; a++) {
        var p0 = pts[a], p1 = pts[(a + 1) % 3];
        for (var t = 0.12; t < 1; t += 0.18) {
          // ⚠️ 配平：每點 44 → 10（第一版 ×14.14，全場最誇張）。等級成長寫在上面 `dmg`。
          areaDamage(p0.x + (p1.x - p0.x) * t, p0.z + (p1.z - p0.z) * t, 3.2, dmg, 0x5fe3ff, null, 'u-swarmnet');
        }
        st.d[a].muzzle.visible = true;
      }
      /* Lv3 拿掉的第二條限制：三條邊線之外完全安全，你腳下（三角形中心）
         永遠是死角。取「你 → 每台僚機」連線的中點各灑一次，不逐點取樣
         （只加 3 個點是配平過的量，取樣太密會讓 Lv3/Lv1 超出 1.9× 上限）。 */
      if (lv >= 3) {
        for (var s = 0; s < 3; s++) {
          areaDamage(P.pos.x + (pts[s].x - P.pos.x) * 0.5, P.pos.z + (pts[s].z - P.pos.z) * 0.5, 3.2, dmg, 0x5fe3ff, null, 'u-swarmnet');
        }
      }
      spawnRing(P.pos.x, P.pos.z, 0x5fe3ff, R * 0.4, R * 1.05, 0.24);
    }
  }
];
